import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import api from '../../utils/api';
import {
  EntityFormDialog,
  FormDialogHeader,
  FormDialogContent,
  FormDialogFooter
} from 'components/form/EntityFormDialog';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import { infoAlertSx } from 'utils/designTokens';
import { useToast } from 'contexts/ToastContext';
import OfferTargetSelect from './OfferTargetSelect';
import {
  buildOfferEditPayload,
  buildFormFromOffer,
  emptyForm,
  getOfferType
} from './offerUtils';

function OfferEditForm({ form, setForm }) {
  const ot = getOfferType(form.offer_type);
  const canEditTargets = form.offer_type === 'item' || form.offer_type === 'category';

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3, ...infoAlertSx }}>
        {canEditTargets
          ? 'You can update validity dates and change the selected products or categories. Other details are read-only.'
          : 'Only the offer validity dates can be updated. Other details are read-only.'}
      </Alert>

      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Stack spacing={1.25}>
          <Typography variant="subtitle2" fontWeight={700}>
            {form.offer_name_en || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Type: {ot.label} · Value:{' '}
            {form.offer_value ? (
              form.offer_value_type === 'percentage' ? (
                `${form.offer_value}%`
              ) : (
                <CurrencyAmount value={form.offer_value} variant="inherit" />
              )
            ) : (
              '—'
            )}
          </Typography>
        </Stack>
      </Paper>

      {canEditTargets && (
        <Box sx={{ mb: 3 }}>
          {!form.store_id ? (
            <Alert severity="warning" sx={{ ...infoAlertSx, mb: 2 }}>
              Store could not be resolved for this offer, so products/categories cannot be loaded.
            </Alert>
          ) : null}
          <OfferTargetSelect
            key={`${form.store_id}-${form.offer_type}`}
            form={form}
            setForm={setForm}
            seedOptions={form.line_seed_options}
            title={form.offer_type === 'item' ? 'Products' : 'Categories'}
          />
        </Box>
      )}

      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
        Offer Details
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description (English)"
            name="description"
            value={form.description || ''}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Describe the offer details..."
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description (Arabic)"
            name="description_ar"
            value={form.description_ar || ''}
            onChange={(e) => setForm((p) => ({ ...p, description_ar: e.target.value }))}
            inputProps={{ dir: 'rtl' }}
            placeholder="وصف العرض بالعربية..."
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
        Validity period
      </Typography>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid size={{ xs: 12, sm: 6 }}>
          <DateTimePicker
            label="Start Date & Time"
            value={form.from_datetime}
            onChange={(v) => setForm((p) => ({ ...p, from_datetime: v }))}
            slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DateTimePicker
            label="End Date & Time"
            value={form.to_datetime}
            onChange={(v) => setForm((p) => ({ ...p, to_datetime: v }))}
            slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
            minDateTime={form.from_datetime || undefined}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

OfferEditForm.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired
};

export default function OfferEditDialog({ open, offerId, listOffer, onClose, onSaved }) {
  const { showToast, showApiError } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open || !offerId) return;

    setLoading(true);
    setForm(emptyForm());

    api
      .get(`/offer/${offerId}`)
      .then(async (res) => {
        const body = res.data?.data ?? res.data;
        const d = body?.offer ?? body;
        if (!d || typeof d !== 'object') {
          showToast('Offer not found', { severity: 'error' });
          onClose();
          return;
        }
        setForm(await buildFormFromOffer(d, listOffer));
      })
      .catch(() => {
        showToast('Failed to load offer data', { severity: 'error' });
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, offerId, listOffer, onClose, showToast]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (
      !form.from_datetime ||
      !form.to_datetime ||
      !dayjs(form.to_datetime).isAfter(form.from_datetime)
    ) {
      showToast('Please set a valid start and end date.', { severity: 'error' });
      return;
    }
    if (
      (form.offer_type === 'item' || form.offer_type === 'category') &&
      form.line_ids.length === 0
    ) {
      showToast(
        form.offer_type === 'item'
          ? 'Please select at least one product.'
          : 'Please select at least one category.',
        { severity: 'error' }
      );
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/offer/edit/${offerId}`, buildOfferEditPayload(form));
      showToast('Offer updated successfully', { severity: 'success' });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      showApiError(err, 'Save failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <EntityFormDialog open={open} onClose={handleClose} maxWidth="md">
        <FormDialogHeader
          icon={<LocalOfferIcon />}
          title="Edit Offer"
          onClose={handleClose}
        />
        <FormDialogContent loading={loading}>
          {!loading ? <OfferEditForm form={form} setForm={setForm} /> : null}
        </FormDialogContent>
        <FormDialogFooter
          onCancel={handleClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Save changes"
        />
      </EntityFormDialog>
    </LocalizationProvider>
  );
}

OfferEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  offerId: PropTypes.string,
  listOffer: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func
};
